import type { MakeBridgeContext } from '../types';
import type { TBridgeMethod } from './types';
import { getAppType } from './utils';

// ─── iframeAnalytics ─────────────────────────────────────────────────────────

type IframeAnalyticsContext = MakeBridgeContext<never, 'analytics'>;

/**
 * Forwards an untrusted performance/analytics event from the Forge iframe
 * to the host-side performance analytics pipeline.
 *
 * The payload is treated as opaque and validated downstream by
 * `captureUntrustedEvent`; no validation is performed here.
 */
export const iframeAnalytics: TBridgeMethod<IframeAnalyticsContext, unknown, void> = async (
	context,
	payload,
) => {
	context.services.analytics.captureUntrustedEvent(payload);
};

// ─── ufoEvent ─────────────────────────────────────────────────────────────────

/**
 * Forwards an untrusted UFO (Unified Frontend Observability) performance event
 * from the Forge iframe to the host-side performance analytics pipeline.
 *
 * Functionally identical to `iframeAnalytics` but exposed under the separate
 * `ufo-event` key so UFO events can be independently routed or filtered
 * downstream. The payload is treated as opaque; no validation is performed here.
 */
export const ufoEvent: TBridgeMethod<IframeAnalyticsContext, unknown, void> = async (
	context,
	payload,
) => {
	context.services.analytics.captureUntrustedEvent(payload);
};

// ─── trackObjectStoreAction ───────────────────────────────────────────────────

type TrackObjectStoreActionPayload = {
	action: 'upload' | 'download' | 'getMetadata' | 'delete';
};

type TrackObjectStoreActionContext = MakeBridgeContext<
	'extension' | 'extensionDetails',
	'analytics'
>;

/**
 * Fires an analytics event for an object store action (upload, download,
 * getMetadata, or delete) performed by the Forge app.
 */
export const trackObjectStoreAction: TBridgeMethod<
	TrackObjectStoreActionContext,
	TrackObjectStoreActionPayload,
	void
> = async (context, payload) => {
	const {
		extension,
		extensionDetails: { appId },
		services: { analytics },
	} = context;

	analytics.tracker.trackObjectStoreAction({
		appType: getAppType(extension),
		appId,
		action: payload.action,
		forgeEnvironment: extension.environmentType,
	});
};
